import { defineMessages } from 'react-intl';

import actionMessages from '../pages/returns-page/enhanced-returns/grids/column-components/messages';

/**
 * Action types that can be associated with error states
 */
export const ErrorActionType = {
  OPEN_SUBSCRIPTION: 'OPEN_SUBSCRIPTION',
  REJECT_RETURN: 'REJECT_RETURN',
  CANCEL_RETURN: 'CANCEL_RETURN',
  RETRY_RETURN: 'RETRY_RETURN',
};

const messages = defineMessages({
  insufficientBalanceTitle: {
    description: 'Title for insufficient balance error when creating a return label',
    defaultMessage: 'Add Funds to Wallet',
    id: '5e7a8I',
  },
  insufficientBalanceMessage: {
    description: 'Message for insufficient balance error when creating a return label',
    defaultMessage: 'We couldn’t create the label because your ShipStation Wallet has insufficient funds. Please add funds to continue.',
    id: 'eocKvA',
  },
  addFundsToWallet: {
    description: 'Action text for insufficient balance error when creating a return label',
    defaultMessage: 'Add Funds',
    id: 'S9tfl+',
  },
  addWallet: {
    description: 'Action text for no wallet added error when creating a return label',
    defaultMessage: 'Setup Wallet',
    id: 'H0Z6by',
  },
  updateAddress: {
    description: 'Action text for invalid address',
    defaultMessage: 'Update Address',
    id: '6Ku2+G',
  },
  verifyEmail: {
    description: 'Action text for unverified email error when creating a return label',
    defaultMessage: 'Verify Email',
    id: 'te2751',
  },
  internationalTitle: {
    description: 'Title for international error when creating a return label',
    defaultMessage: 'International Shipping Issue',
    id: 'ALsQgN',
  },
  internationalMessage: {
    description: 'Message for international error when creating a return label',
    defaultMessage: "Shipper's ShipperNumber must be the same as the shipment's Shipper's country",
    id: 'Dvnj2l',
  },
  multipleRequestServiceLevelsTitle: {
    description: 'Title for multiple request service levels error when creating a return label',
    defaultMessage: 'Multiple Request Service Levels',
    id: '459e+v',
  },
  multipleRequestServiceLevelsMessage: {
    description: 'Message for multiple request service levels error when creating a return label',
    defaultMessage: 'returned more than one RequestServiceLevel',
    id: 'EEVd1F',
  },
  shipmentAlreadyPurchasedTitle: {
    description: 'Title for shipment already purchased error when creating a return label',
    defaultMessage: 'Already Purchased',
    id: 'vlpY9h',
  },
  shipmentAlreadyPurchasedMessage: {
    description: 'Message for shipment already purchased error when creating a return label',
    defaultMessage: 'Can not purchase label since a label has already been created',
    id: '42vbAJ',
  },
  unsupportedLayoutTitle: {
    description: 'Title for unsupported layout error when creating a return label',
    defaultMessage: 'Layout Error',
    id: 'J1oCi/',
  },
  unsupportedLayoutMessage: {
    description: 'Message for unsupported layout error when creating a return label',
    defaultMessage: 'unsupported layout',
    id: 'M+Cp7f',
  },
  accessDeniedTitle: {
    description: 'Title for access denied error when creating a return label',
    defaultMessage: 'Access Denied',
    id: 'd0hIL2',
  },
  accessDeniedMessage: {
    description: 'Message for access denied error when creating a return label',
    defaultMessage: 'Access denied',
    id: 'Jdy8Ta',
  },
  noWalletAddedTitle: {
    description: 'Title for no wallet added error when creating a return label',
    defaultMessage: 'Connect Carrier Account',
    id: 'M82P4H',
  },
  noWalletAddedMessage: {
    description: 'Message for no wallet added error when creating a return label',
    defaultMessage:
      'We couldn’t create the label because no carrier account is connected. Please connect your ShipStation carrier account to continue.',
    id: '4S/CUe',
  },
  invalidPostalCodeTitle: {
    description: 'Title for invalid postal code error when creating a return label',
    defaultMessage: 'Invalid Address',
    id: '7XUHE0',
  },
  invalidPostalCodeMessage: {
    description: 'Message for invalid postal code error when creating a return label',
    defaultMessage: 'The postal code provided is invalid. Please check and try again.',
    id: 'Pt70IM',
  },
  phoneRequiredTitle: {
    description: 'Title for phone required error when creating a return label',
    defaultMessage: 'Phone Number Required',
    id: 'qLYL1h',
  },
  phoneRequiredMessage: {
    description: 'Message for phone required error when creating a return label',
    defaultMessage: 'A phone number is required to create a return label. Please provide a valid phone number and try again.',
    id: '2kaRxG',
  },
  unverifiedEmailTitle: {
    description: 'Title for unverified email error when creating a return label',
    defaultMessage: 'Verify ShipStation Account',
    id: 'pQAixx',
  },
  unverifiedEmailMessage: {
    description: 'Message for unverified email error when creating a return label',
    defaultMessage:
      'We couldn’t create the label because your email address isn’t verified. We’ve sent a verification link to your account email. Didn’t receive it? Check your spam folder, or send it again. Once verified, return to ShipStation to continue.',
    id: 'CYYcaP',
  },
  customsFormRequiredTitle: {
    description: 'Title for customs form required error when creating a return label',
    defaultMessage: 'Add Customs Information',
    id: 'AwovcB',
  },
  customsFormRequiredMessage: {
    description: 'Message for customs form required error when creating a return label',
    defaultMessage:
      'We couldn’t create the label because customs details are required for this international return. Please add the customs information to continue.',
    id: 'ySALDB',
  },
  nameRequiredTitle: {
    description: 'Title for name required error when creating a return label',
    defaultMessage: 'Invalid Address',
    id: 'XiK8Ti',
  },
  nameRequiredMessage: {
    description: 'Message for name required error when creating a return label',
    defaultMessage: 'Missing or invalid ship from attention name',
    id: 'zBo39T',
  },
  poBoxNotAllowedTitle: {
    description: 'Title for PO Box not allowed error when creating a return label',
    defaultMessage: 'Invalid Address',
    id: 'qc01WZ',
  },
  poBoxNotAllowedMessage: {
    description: 'Message for PO Box not allowed error when creating a return label',
    defaultMessage:
      'PO Box addresses are not allowed for the selected carrier or service. Please provide a different address and try again.',
    id: '2K5Efh',
  },
  invalidAddressLine1Title: {
    description: 'Title for invalid address line 1 error when creating a return label',
    defaultMessage: 'Invalid Address',
    id: 'kkb2P/',
  },
  invalidAddressLine1Message: {
    description: 'Message for invalid address line 1 error when creating a return label',
    defaultMessage: 'Missing or invalid ship from address line 1',
    id: 'Jb0BBM',
  },
  invalidCityTitle: {
    description: 'Title for invalid city error when creating a return label',
    defaultMessage: 'Invalid Address',
    id: 'Fd+t3s',
  },
  invalidCityMessage: {
    description: 'Message for invalid city error when creating a return label',
    defaultMessage: 'Missing or invalid ship from city',
    id: 'jHp0ZY',
  },
  invalidAddressLine2Title: {
    description: 'Title for invalid address line 2 error when creating a return label',
    defaultMessage: 'Invalid Address',
    id: 'EY040A',
  },
  invalidAddressLine2Message: {
    description: 'Message for invalid address line 2 error when creating a return label',
    defaultMessage: 'Missing or invalid ship from address line 2',
    id: 'J4+LEo',
  },
  shipengineTimeoutTitle: {
    description: 'Title for ShipEngine timeout error when creating a return label',
    defaultMessage: 'Something Went Wrong',
    id: '4dNFRO',
  },
  shipengineTimeoutMessage: {
    description: 'Message for ShipEngine timeout error when creating a return label',
    defaultMessage: 'Request timeout. Please try again.',
    id: 'PY2rY4',
  },
  invalidCarrierIdTitle: {
    description: 'Title for invalid carrier ID error when creating a return label',
    defaultMessage: 'Something Went Wrong',
    id: '7gWirp',
  },
  invalidCarrierIdMessage: {
    description: 'Message for invalid carrier ID error when creating a return label',
    defaultMessage: 'Invalid carrier ID.',
    id: 'l1hBdE',
  },
  invalidCountryCodeTitle: {
    description: 'Title for invalid country code error when creating a return label',
    defaultMessage: 'Invalid Country Code',
    id: 'r5NOF7',
  },
  invalidCountryCodeMessage: {
    description: 'Message for invalid country code error when creating a return label',
    defaultMessage: 'The country code provided is invalid. Please check and try again.',
    id: 'jWgPSq',
  },
  invalidPackageTitle: {
    description: 'Title for invalid package error when creating a return label',
    defaultMessage: 'Invalid Package Type',
    id: 'Dk0GJx',
  },
  invalidPackageMessage: {
    description: 'Message for invalid package error when creating a return label',
    defaultMessage: 'The package type for the selected carrier is invalid.',
    id: 'nsuN16',
  },
  somethingWrongTitle: {
    description: 'Title for something went wrong error when creating a return label',
    defaultMessage: 'Something Went Wrong',
    id: 'ZZjoZn',
  },
  somethingWrongMessage: {
    description: 'Message for something went wrong error when creating a return label',
    defaultMessage: 'Something went wrong and we are looking into it.',
    id: 'DXdSZh',
  },
  noRatesAvailableTitle: {
    description: 'Title for no rates available error when creating a return label',
    defaultMessage: 'No Rates Available',
    id: '0ZdxHd',
  },
  noRatesAvailableMessage: {
    description: 'Message for no rates available error when creating a return label',
    defaultMessage: 'No shipping rates are available',
    id: 'uLLa8M',
  },
  invalidDimensionsTitle: {
    description: 'Title for invalid dimensions error when creating a return label',
    defaultMessage: 'Invalid Dimensions',
    id: 'pMQgMp',
  },
  invalidDimensionsMessage: {
    description: 'Message for invalid dimensions error when creating a return label',
    defaultMessage: 'The dimensions provided are invalid. Please check and try again.',
    id: 'TNCrhV',
  },
  invalidWeightTitle: {
    description: 'Title for invalid weight error when creating a return label',
    defaultMessage: 'Invalid Weight',
    id: 'FBUvb6',
  },
  invalidWeightMessage: {
    description: 'Message for invalid weight error when creating a return label',
    defaultMessage: 'The weight provided is invalid. Please check and try again.',
    id: 'hBd8Bh',
  },
  trialExpiredTitle: {
    description: 'Title for trial expired error when creating a return label',
    defaultMessage: 'Upgrade Your Plan',
    id: 'VSvVGM',
  },
  trialExpiredMessage: {
    description: 'Message for trial expired error',
    defaultMessage: 'Your free trial has expired. To continue using Shipstation Returns and Exchanges, upgrade your plan type.',
    id: 'xKH3WL',
  },
  selectPlan: {
    description: 'Action text for selecting a plan',
    defaultMessage: 'Select Plan',
    id: 'frO79j',
  },
  unknownErrorLabel: {
    id: '/PEoRL',
    description: 'Error label for when an unknown error occurs',
    defaultMessage: 'Unknown Error',
  },
  unknownErrorMessage: {
    id: 'GXl963',
    description: 'Error message for when an unknown error occurs',
    defaultMessage: 'An unknown error occurred. Please try again.',
  },
  alreadyOnHoldTitle: {
    description: 'Title for already on hold error',
    defaultMessage: 'Already On Hold',
    id: 'Xb8mvy',
  },
  alreadyOnHoldMessage: {
    description: 'Message for already on hold error',
    defaultMessage: 'This request cannot be processed since Order Service is in Hold State',
    id: 'bm02aQ',
  },
  exchangeAlreadyRejectedTitle: {
    description: 'Title for exchange already rejected error',
    defaultMessage: 'Exchange Already Rejected',
    id: '27A0Rt',
  },
  exchangeAlreadyRejectedMessage: {
    description: 'Message for exchange already rejected error',
    defaultMessage: 'This exchange has already been rejected.',
    id: 'AJwKEh',
  },
  exchangeApprovedMarkedForResolutionTitle: {
    description: 'Title for exchange approved and marked for resolution error',
    defaultMessage: 'Needs Resolution',
    id: '2CM5qy',
  },
  exchangeApprovedMarkedForResolutionMessage: {
    description: 'Message for exchange approved and marked for resolution error',
    defaultMessage: 'This exchange has been approved or marked for resolution.',
    id: 'z+6dKB',
  },
  exchangeMarkedForPaymentResolutionTitle: {
    description: 'Title for exchange marked for payment resolution error',
    defaultMessage: 'Payment Resolution Needed',
    id: '4/Jg6S',
  },
  exchangeMarkedForPaymentResolutionMessage: {
    description: 'Message for exchange marked for payment resolution error',
    defaultMessage: 'This exchange is marked for payment resolution.',
    id: 'Pap2Qu',
  },
  exchangeOrderAlreadyCreatedTitle: {
    description: 'Title for exchange order already created error',
    defaultMessage: 'Exchange Order Already Created',
    id: 'ZjrGwn',
  },
  exchangeOrderAlreadyCreatedMessage: {
    description: 'Message for exchange order already created error',
    defaultMessage: 'An exchange order has already been created for this return.',
    id: 'bmKWqJ',
  },
  exchangeRejectedSuccessfullyTitle: {
    description: 'Title for exchange rejected successfully',
    defaultMessage: 'Exchange Rejected',
    id: 'k34QY1',
  },
  exchangeRejectedSuccessfullyMessage: {
    description: 'Message for exchange rejected successfully',
    defaultMessage: 'This exchange has been rejected.',
    id: '/xmVOo',
  },
  giftcardCreationFailedAlreadyProcessedTitle: {
    description: 'Title for gift card creation failed - already processed error',
    defaultMessage: 'Something Went Wrong',
    id: '+Qe5Y4',
  },
  giftcardCreationFailedAlreadyProcessedMessage: {
    description: 'Message for gift card creation failed - already processed error',
    defaultMessage:
      "We couldn't process the request because Shopify's rate limit was exceeded. Please retry in a few seconds, or contact ShipStation Support if the issue continues.",
    id: '8Sjm9w',
  },
  insufficientExchangeVariantQtyOrVariantDeletedTitle: {
    description: 'Title for insufficient exchange variant quantity or variant deleted error',
    defaultMessage: 'Out of Stock',
    id: 'Uovsv9',
  },
  insufficientExchangeVariantQtyOrVariantDeletedMessage: {
    description: 'Message for insufficient exchange variant quantity or variant deleted error',
    defaultMessage:
      "We couldn't process the request because the exchange item is either out of stock or the product was deleted. Please add items back in inventory on {storeName}.",
    id: 'LDN95X',
  },
  invalidEntityTypeTitle: {
    description: 'Title for invalid entity type error',
    defaultMessage: 'Invalid Entity Type',
    id: 'Xn7vpe',
  },
  /**
   * Users shouldn't be seeing this error in normal circumstances.
   * Triggered when a refund is trying to be moved and there is a race
   * condition on SSR.
   */
  invalidEntityTypeMessage: {
    description: 'Message for invalid entity type error',
    defaultMessage: 'The refund was successful but invalid `entity_type`',
    id: '26ILtx',
  },
  orderOlderThan60DaysTitle: {
    description: 'Title for order older than 60 days error',
    defaultMessage: 'Order Too Old',
    id: 'A63PB2',
  },
  orderOlderThan60DaysMessage: {
    description: 'Message for order older than 60 days error',
    defaultMessage: 'Since the order is older than 60 days, it can only be refunded through gift card.',
    id: 'dOJV9q',
  },
  osInRejectionStageTitle: {
    description: 'Title for order in rejection stage error',
    defaultMessage: 'Return Processing Problem',
    id: '8J8zIi',
  },
  osInRejectionStageMessage: {
    description: 'Message for order in rejection stage error',
    defaultMessage: 'Order Service is in rejection state and the return can not be processed.',
    id: 'Bdqbi/',
  },
  paymentResolutionFailedTitle: {
    description: 'Title for payment resolution failed error',
    defaultMessage: 'Payment Resolution Failed',
    id: 'P+kDFK',
  },
  paymentResolutionFailedMessage: {
    description: 'Message for payment resolution failed error',
    defaultMessage: 'Order service processing failed.',
    id: 'nBxzKj',
  },
  refundAlreadyCreatedTitle: {
    description: 'Title for refund already created error',
    defaultMessage: 'Refund Already Created',
    id: '1zbAUh',
  },
  refundAlreadyCreatedMessage: {
    description: 'Message for refund already created error',
    defaultMessage: 'Refund already created against this order service',
    id: 'yOET+O',
  },
  refundAlreadyMarkedForResolutionTitle: {
    description: 'Title for refund already marked for resolution error',
    defaultMessage: 'Refund Already Marked for Resolution',
    id: 'g11fc7',
  },
  refundAlreadyMarkedForResolutionMessage: {
    description: 'Message for refund already marked for resolution error',
    defaultMessage: 'Refund already marked for resolution/refunded',
    id: '+VhBe8',
  },
  refundAmountExceedsMaxAmountTitle: {
    description: 'Title for refund amount exceeds max amount error',
    defaultMessage: 'Refund Failed',
    id: 'RgpvrH',
  },
  refundAmountExceedsMaxAmountMessage: {
    description: 'Message for refund amount exceeds max amount error',
    defaultMessage:
      'There are not enough funds left on this order to process the refund through ShipStation. Please issue the refund manually in {storeName} and cancel this request.',
    id: 'bsUo+M',
  },
  refundApprovalRejectedSuccessTitle: {
    description: 'Title for refund approval rejected successfully',
    defaultMessage: 'Refund Approval Rejected',
    id: 'XvY/0l',
  },
  refundApprovalRejectedSuccessMessage: {
    description: 'Message for refund approval rejected successfully',
    defaultMessage: 'The refund approval has been rejected.',
    id: 'BjEjea',
  },
  refundToRechargeApiFailedTitle: {
    description: 'Title for refund to recharge API failed error',
    defaultMessage: 'Refund Processing Failed',
    id: 'ir8gKz',
  },
  refundToRechargeApiFailedMessage: {
    description: 'Message for refund to recharge API failed error',
    defaultMessage: 'Some error occurred while posting refund to recharge APIs.',
    id: 'aCN2Ap',
  },
  retexDoesNotExistTitle: {
    description: 'Title for return exchange does not exist error',
    defaultMessage: 'Item Not Found',
    id: 'xG7ZAr',
  },
  retexDoesNotExistMessage: {
    description: 'Message for return exchange does not exist error',
    defaultMessage: 'An item for this return could not be found.',
    id: 'IvxZ+U',
  },
  retexNotLastRetexTitle: {
    description: 'Title for return exchange not last return exchange error',
    defaultMessage: 'Item Issue',
    id: 'A1qsUx',
  },
  retexNotLastRetexMessage: {
    description: 'Message for return exchange not last return exchange error',
    defaultMessage: 'Perform Payment Resolution function called by the retex which is not the last retex in the order service.',
    id: 'V3fzVo',
  },
  returnMarkedForPaymentResolutionTitle: {
    description: 'Title for return marked for payment resolution error',
    defaultMessage: 'Needs Payment Resolution',
    id: 'yXN5Tb',
  },
  returnMarkedForPaymentResolutionMessage: {
    description: 'Message for return marked for payment resolution error',
    defaultMessage: 'This return is marked for payment resolution.',
    id: 'pJJYWb',
  },
  statusAlreadyUpdatedTitle: {
    description: 'Title for status already updated error',
    defaultMessage: 'Status Already Updated',
    id: '6oPgqc',
  },
  statusAlreadyUpdatedMessage: {
    description: 'Message for status already updated error',
    defaultMessage: 'The status has already been updated.',
    id: 'uOTPoj',
  },
  unableToMoveToAwaitingPaymentCompletionTitle: {
    description: 'Title for unable to move to awaiting payment completion error',
    defaultMessage: 'Cannot Update Status',
    id: 'NSKqeR',
  },
  unableToMoveToAwaitingPaymentCompletionMessage: {
    description: 'Message for unable to move to awaiting payment completion error',
    defaultMessage: 'Unable to change an item to Awaiting Payment Completion.',
    id: 'd+AMzd',
  },
  unableToMoveToExchangeSuccessTitle: {
    description: 'Title for unable to move to exchange success error',
    defaultMessage: 'Cannot Update Status',
    id: '44xTJv',
  },
  unableToMoveToExchangeSuccessMessage: {
    description: 'Message for unable to move to exchange success error',
    defaultMessage: 'The refund was successful, but we were unable to change an item to exchange success.',
    id: 'i5gYVD',
  },
  unableToMoveToPendingPaymentCompletionTitle: {
    description: 'Title for unable to move to pending payment completion error',
    defaultMessage: 'Cannot Update Status',
    id: 'Xdcgkp',
  },
  unableToMoveToPendingPaymentCompletionMessage: {
    description: 'Message for unable to move to pending payment completion error',
    defaultMessage: 'Unable to move an item to pending payment completion.',
    id: 'Hg6oOC',
  },
  unableToMoveToProcessedTitle: {
    description: 'Title for unable to move to processed error',
    defaultMessage: 'Cannot Update Status',
    id: '0phRc/',
  },
  unableToMoveToProcessedMessage: {
    description: 'Message for unable to move to processed error',
    defaultMessage: 'The refund was successful but unable to change order service.',
    id: 'StQZpT',
  },
  unableToMoveToPurchaseSuccessTitle: {
    description: 'Title for unable to move to purchase success error',
    defaultMessage: 'Cannot Update Status',
    id: '4xDotx',
  },
  unableToMoveToPurchaseSuccessMessage: {
    description: 'Message for unable to move to purchase success error',
    defaultMessage: 'The refund was successful, but we were unable to change the status of a return or exchange item to purchased.',
    id: 'G+1IGA',
  },
  unableToMoveToSuccessTitle: {
    description: 'Title for unable to move to success error',
    defaultMessage: 'Cannot Update Status',
    id: 'v3T2s9',
  },
  unableToMoveToSuccessMessage: {
    description: 'Message for unable to move to success error',
    defaultMessage: 'The refund was successful, but unable to change status of the return or exchange item.',
    id: 'XYlSIf',
  },
  checkInventory: {
    description: 'Action text for checking inventory when exchange variant quantity is insufficient or deleted',
    defaultMessage: 'Check Inventory',
    id: 'CUZeR0',
  },
  generateLabel: {
    description: 'Action text for generating a label after label generation failure',
    defaultMessage: 'Generate Label',
    id: 'wIA2li',
  },
  retryProcessing: {
    description: 'Action text for retrying return processing for Shopify and other errors',
    defaultMessage: 'Retry Processing',
    id: 'oBusL8',
  },
  labelGenerationErrorTitle: {
    description: 'Title for label generation error',
    defaultMessage: 'Label Generation Failed',
    id: 'vmIXhS',
  },
  labelGenerationErrorMessage: {
    description: 'Message for label generation error',
    defaultMessage: 'Failed to generate the shipping label.',
    id: '0Pp6x/',
  },
  retryingLabelGenerationTitle: {
    description: 'Title for retrying label generation',
    defaultMessage: 'Retrying Label Generation',
    id: '0QXCpt',
  },
  retryingLabelGenerationMessage: {
    description: 'Message for retrying label generation',
    defaultMessage: 'We are retrying the label generation.',
    id: '2UrlOm',
  },
});

export const EnhancedReturnNeedsAttentionEnums = {
  InsufficientBalance: {
    title: messages.insufficientBalanceTitle,
    message: messages.insufficientBalanceMessage,
    actionText: messages.addFundsToWallet,
    key: 'insufficient_balance',
    action: null,
  },
  International: {
    title: messages.internationalTitle,
    message: messages.internationalMessage,
    actionText: null,
    key: 'international',
    action: null,
  },
  MultipleRequestServiceLevels: {
    title: messages.multipleRequestServiceLevelsTitle,
    message: messages.multipleRequestServiceLevelsMessage,
    actionText: null,
    key: 'rr_multiple_rsl',
    action: null,
  },
  ShipmentAlreadyPurchased: {
    title: messages.shipmentAlreadyPurchasedTitle,
    message: messages.shipmentAlreadyPurchasedMessage,
    key: 'shipment_already_purchased',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  UnsupportedLayout: {
    title: messages.unsupportedLayoutTitle,
    message: messages.unsupportedLayoutMessage,
    key: 'unsupported_layout',
    actionText: null,
    action: null,
  },
  AccessDenied: {
    title: messages.accessDeniedTitle,
    message: messages.accessDeniedMessage,
    key: 'access_denied',
    actionText: null,
    action: null,
  },
  NoWalletAdded: {
    title: messages.noWalletAddedTitle,
    message: messages.noWalletAddedMessage,
    key: 'no_wallet_added',
    actionText: messages.addWallet,
    action: null,
  },
  InvalidPostalCode: {
    title: messages.invalidPostalCodeTitle,
    message: messages.invalidPostalCodeMessage,
    key: 'invalid_postal_code',
    actionText: messages.updateAddress,
    action: null,
  },
  PhoneRequired: {
    title: messages.phoneRequiredTitle,
    message: messages.phoneRequiredMessage,
    key: 'phone_required',
    actionText: messages.updateAddress,
    action: null,
  },
  UnverifiedEmail: {
    title: messages.unverifiedEmailTitle,
    message: messages.unverifiedEmailMessage,
    key: 'unverified_email',
    actionText: messages.verifyEmail,
    action: null,
  },
  CustomsFormRequired: {
    title: messages.customsFormRequiredTitle,
    message: messages.customsFormRequiredMessage,
    key: 'customs_form_required',
    actionText: null,
    action: null,
  },
  NameRequired: {
    title: messages.nameRequiredTitle,
    message: messages.nameRequiredMessage,
    key: 'name_required',
    actionText: messages.updateAddress,
    action: null,
  },
  PoBoxNotAllowed: {
    title: messages.poBoxNotAllowedTitle,
    message: messages.poBoxNotAllowedMessage,
    key: 'po_box_not_allowed',
    actionText: messages.updateAddress,
    action: null,
  },
  InvalidAddressLine1: {
    title: messages.invalidAddressLine1Title,
    message: messages.invalidAddressLine1Message,
    key: 'invalid_address_line1',
    actionText: messages.updateAddress,
    action: null,
  },
  InvalidCity: {
    title: messages.invalidCityTitle,
    message: messages.invalidCityMessage,
    key: 'invalid_city',
    actionText: messages.updateAddress,
    action: null,
  },
  InvalidAddressLine2: {
    title: messages.invalidAddressLine2Title,
    message: messages.invalidAddressLine2Message,
    key: 'invalid_address_line2',
    actionText: messages.updateAddress,
    action: null,
  },
  ShipengineTimeout: {
    title: messages.shipengineTimeoutTitle,
    message: messages.shipengineTimeoutMessage,
    key: 'shipengine_timeout',
    actionText: messages.retryProcessing,
    action: ErrorActionType.RETRY_RETURN,
  },
  InvalidCarrierId: {
    title: messages.invalidCarrierIdTitle,
    message: messages.invalidCarrierIdMessage,
    key: 'invalid_carrier_id',
    actionText: null,
    action: null,
  },
  InvalidCountryCode: {
    title: messages.invalidCountryCodeTitle,
    message: messages.invalidCountryCodeMessage,
    key: 'invalid_country_code',
    actionText: messages.updateAddress,
    action: null,
  },
  InvalidPackage: {
    title: messages.invalidPackageTitle,
    message: messages.invalidPackageMessage,
    key: 'invalid_package',
    actionText: null,
    action: null,
  },
  SomethingWrong: {
    title: messages.somethingWrongTitle,
    message: messages.somethingWrongMessage,
    key: 'something_wrong',
    actionText: messages.retryProcessing,
    action: ErrorActionType.RETRY_RETURN,
  },
  NoRatesAvailable: {
    title: messages.noRatesAvailableTitle,
    message: messages.noRatesAvailableMessage,
    key: 'no_rates_available',
    actionText: null,
    action: null,
  },
  InvalidDimensions: {
    title: messages.invalidDimensionsTitle,
    message: messages.invalidDimensionsMessage,
    key: 'invalid_dimensions',
    actionText: null,
    action: null,
  },
  InvalidWeight: {
    title: messages.invalidWeightTitle,
    message: messages.invalidWeightMessage,
    key: 'invalid_weight',
    actionText: null,
    action: null,
  },
  TrialExpired: {
    title: messages.trialExpiredTitle,
    message: messages.trialExpiredMessage,
    key: 'trial_expired',
    actionText: messages.selectPlan,
    action: ErrorActionType.OPEN_SUBSCRIPTION,
  },
  Unknown: {
    title: messages.unknownErrorLabel,
    message: messages.unknownErrorMessage,
    key: 'unknown',
    actionText: messages.retryProcessing,
    action: ErrorActionType.RETRY_RETURN,
  },
  AlreadyOnHold: {
    title: messages.alreadyOnHoldTitle,
    message: messages.alreadyOnHoldMessage,
    key: 'ALREADY_ON_HOLD',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  ExchangeAlreadyRejected: {
    title: messages.exchangeAlreadyRejectedTitle,
    message: messages.exchangeAlreadyRejectedMessage,
    key: 'EXCHANGE_ALREADY_REJECTED',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  ExchangeApprovedMarkedForResolution: {
    title: messages.exchangeApprovedMarkedForResolutionTitle,
    message: messages.exchangeApprovedMarkedForResolutionMessage,
    key: 'EXCHANGE_APPROVED_MARKED_FOR_RESOLUTION',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  ExchangeMarkedForPaymentResolution: {
    title: messages.exchangeMarkedForPaymentResolutionTitle,
    message: messages.exchangeMarkedForPaymentResolutionMessage,
    key: 'EXCHANGE_MARKED_FOR_PAYMENT_RESOLUTION',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  ExchangeOrderAlreadyCreated: {
    title: messages.exchangeOrderAlreadyCreatedTitle,
    message: messages.exchangeOrderAlreadyCreatedMessage,
    key: 'EXCHANGE_ORDER_ALREADY_CREATED',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  ExchangeRejectedSuccessfully: {
    title: messages.exchangeRejectedSuccessfullyTitle,
    message: messages.exchangeRejectedSuccessfullyMessage,
    key: 'EXCHANGE_REJECTED_SUCCESSFULLY',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  GiftcardCreationFailedAlreadyProcessed: {
    title: messages.giftcardCreationFailedAlreadyProcessedTitle,
    message: messages.giftcardCreationFailedAlreadyProcessedMessage,
    key: 'GIFTCARD_CREATION_FAILED_ALREADY_PROCESSED',
    actionText: messages.retryProcessing,
    action: ErrorActionType.RETRY_RETURN,
  },
  InsufficientExchangeVariantQtyOrVariantDeleted: {
    title: messages.insufficientExchangeVariantQtyOrVariantDeletedTitle,
    message: messages.insufficientExchangeVariantQtyOrVariantDeletedMessage,
    key: 'INSUFFICIENT_EXCHANGE_VARIANT_QTY_OR_VARIANT_DELETED',
    actionText: messages.checkInventory,
    action: ErrorActionType.RETRY_RETURN,
  },
  InvalidEntityType: {
    title: messages.invalidEntityTypeTitle,
    message: messages.invalidEntityTypeMessage,
    key: 'INVALID_ENTITY_TYPE',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  OrderOlderThan60Days: {
    title: messages.orderOlderThan60DaysTitle,
    message: messages.orderOlderThan60DaysMessage,
    key: 'ORDER_OLDER_THAN_60_DAYS',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  OsInRejectionStage: {
    title: messages.osInRejectionStageTitle,
    message: messages.osInRejectionStageMessage,
    key: 'OS_IN_REJECTION_STAGE',
    actionText: null,
    action: null,
  },
  PaymentResolutionFailed: {
    title: messages.paymentResolutionFailedTitle,
    message: messages.paymentResolutionFailedMessage,
    key: 'PAYMENT_RESOLUTION_FAILED',
    actionText: messages.retryProcessing,
    action: ErrorActionType.RETRY_RETURN,
  },
  RefundAlreadyCreated: {
    title: messages.refundAlreadyCreatedTitle,
    message: messages.refundAlreadyCreatedMessage,
    key: 'REFUND_ALREADY_CREATED',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  RefundAlreadyMarkedForResolution: {
    title: messages.refundAlreadyMarkedForResolutionTitle,
    message: messages.refundAlreadyMarkedForResolutionMessage,
    key: 'REFUND_ALREADY_MARKED_FOR_RESOLUTION',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  RefundAmountExceedsMaxAmount: {
    title: messages.refundAmountExceedsMaxAmountTitle,
    message: messages.refundAmountExceedsMaxAmountMessage,
    key: 'REFUND_AMOUNT_EXCEEDS_MAX_AMOUNT',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  RefundApprovalRejectedSuccess: {
    title: messages.refundApprovalRejectedSuccessTitle,
    message: messages.refundApprovalRejectedSuccessMessage,
    key: 'REFUND_APPROVAL_REJECTED_SUCCESS',
    actionText: null,
    action: null,
  },
  RefundToRechargeApiFailed: {
    title: messages.refundToRechargeApiFailedTitle,
    message: messages.refundToRechargeApiFailedMessage,
    key: 'REFUND_TO_RECHARGE_API_FAILED',
    actionText: messages.retryProcessing,
    action: ErrorActionType.RETRY_RETURN,
  },
  RetexDoesNotExist: {
    title: messages.retexDoesNotExistTitle,
    message: messages.retexDoesNotExistMessage,
    key: 'RETEX_DOES_NOT_EXIST',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  RetexNotLastRetex: {
    title: messages.retexNotLastRetexTitle,
    message: messages.retexNotLastRetexMessage,
    key: 'RETEX_NOT_LAST_RETEX',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  ReturnMarkedForPaymentResolution: {
    title: messages.returnMarkedForPaymentResolutionTitle,
    message: messages.returnMarkedForPaymentResolutionMessage,
    key: 'RETURN_MARKED_FOR_PAYMENT_RESOLUTION',
    actionText: null,
    action: null,
  },
  StatusAlreadyUpdated: {
    title: messages.statusAlreadyUpdatedTitle,
    message: messages.statusAlreadyUpdatedMessage,
    key: 'STATUS_ALREADY_UPDATED',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  UnableToMoveToAwaitingPaymentCompletion: {
    title: messages.unableToMoveToAwaitingPaymentCompletionTitle,
    message: messages.unableToMoveToAwaitingPaymentCompletionMessage,
    key: 'UNABLE_TO_MOVE_TO_AWAITING_PAYMENT_COMPLETION',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  UnableToMoveToExchangeSuccess: {
    title: messages.unableToMoveToExchangeSuccessTitle,
    message: messages.unableToMoveToExchangeSuccessMessage,
    key: 'UNABLE_TO_MOVE_TO_EXCHANGE_SUCCESS',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  UnableToMoveToPendingPaymentCompletion: {
    title: messages.unableToMoveToPendingPaymentCompletionTitle,
    message: messages.unableToMoveToPendingPaymentCompletionMessage,
    key: 'UNABLE_TO_MOVE_TO_PENDING_PAYMENT_COMPLETION',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  UnableToMoveToProcessed: {
    title: messages.unableToMoveToProcessedTitle,
    message: messages.unableToMoveToProcessedMessage,
    key: 'UNABLE_TO_MOVE_TO_PROCESSED',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  UnableToMoveToPurchaseSuccess: {
    title: messages.unableToMoveToPurchaseSuccessTitle,
    message: messages.unableToMoveToPurchaseSuccessMessage,
    key: 'UNABLE_TO_MOVE_TO_PURCHASE_SUCCESS',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  UnableToMoveToSuccess: {
    title: messages.unableToMoveToSuccessTitle,
    message: messages.unableToMoveToSuccessMessage,
    key: 'UNABLE_TO_MOVE_TO_SUCCESS',
    actionText: actionMessages.cancelRequest,
    action: ErrorActionType.CANCEL_RETURN,
  },
  LabelGenerationError: {
    title: messages.labelGenerationErrorTitle,
    message: messages.labelGenerationErrorMessage,
    key: 'LABEL_GENERATION_ERROR',
    actionText: messages.generateLabel,
    action: ErrorActionType.RETRY_RETURN,
  },
  RetryingLabelGeneration: {
    title: messages.retryingLabelGenerationTitle,
    message: messages.retryingLabelGenerationMessage,
    key: 'RETRYING_LABEL_GENERATION',
    actionText: null,
    action: null,
  },
};

/**
 * Lookup map to get enum by reason code.
 * @type {Record<string, typeof EnhancedReturnNeedsAttentionEnums[keyof typeof EnhancedReturnNeedsAttentionEnums]>}
 * @readonly
 */
export const REASON_CODE_TO_ENUM_MAP = Object.fromEntries(
  Object.values(EnhancedReturnNeedsAttentionEnums).map((enumEntry) => [enumEntry.key, enumEntry]),
);
